/**
 * Centralized application configuration.
 *
 * APP_URL resolves to the current origin at runtime so that referral links,
 * canonical tags and OG metadata always point to wherever the app is actually
 * hosted (e.g. Lovable preview, custom domain, etc.).
 *
 * BRAND_DOMAIN is the vanity domain shown in marketing copy / footer — it
 * stays fixed regardless of hosting.
 */

export const APP_URL =
  typeof window !== "undefined" ? window.location.origin : "https://elarahealth.life";

/** Vanity / marketing domain (for display only, not functional links) */
export const BRAND_DOMAIN = "elarahealth.life";

/** Build a working referral link using the current origin */
export const buildReferralLink = (userId: string) => `${APP_URL}/r/${userId}`;

/** Short display version of a referral link (for UI only) */
export const shortReferralDisplay = (userId: string) =>
  `${BRAND_DOMAIN}/r/${userId.slice(0, 8)}`;

/**
 * Canonical origin used for <link rel="canonical"> and og:url.
 *
 * Always the production domain — never the preview origin — so preview and
 * *.lovable.app copies point their ranking signals at the real page instead of
 * self-canonicalising a duplicate.
 */
export const CANONICAL_ORIGIN = "https://elarahealth.life";

/** Absolute canonical URL for a site-relative path, e.g. canonicalUrl("/about") */
export const canonicalUrl = (path = "/") => {
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  const normalized = withSlash.length > 1 ? withSlash.replace(/\/+$/, "") : "/";
  return `${CANONICAL_ORIGIN}${normalized}`;
};
